import React, { useCallback, useState } from "react";

import { IconButton, Menu, MenuItem, styled } from "@mui/material";
import { BigPlusIcon } from "assets/material-custom-icons";
import { DealCreate } from "components/modals";
import { useActionHook, useModals, useNavigate, usePrompt } from "hooks";
import { useDocumentsActions } from "store/actions-context";
import { ICreateDocumentData } from "store/types";
import { parseDocumentUrlInfo } from "utils/parse-document-url";

const StyledMenuItem = styled(MenuItem)(() => ({
  transition: "all 0.2s linear",
  ":hover": {
    backgroundColor: "#ECF1F8"
  }
}));

interface AddMenuProps {
  refetch: (disableRedirect?: boolean) => void;
}

export const AddMenu: React.FC<AddMenuProps> = ({ refetch }) => {
  const { createGlobalDocumentAction, createDocumentAction } = useDocumentsActions();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { currentTab } = parseDocumentUrlInfo();
  const { createModal, selectDealFolderModal } = useModals();
  const navigate = useNavigate();
  const openPrompt = usePrompt();

  const isDealsDocumentsTab = currentTab === "deals";

  const handleClose = () => {
    setAnchor(null);
  };

  const { action: createGlobalDocument } = useActionHook(createGlobalDocumentAction, {
    onSuccess: (data) => {
      refetch(true);
      navigate({
        route: "documentId",
        params: {
          document_id: data.id,
          tab: currentTab
        }
      });
    }
  });

  const { action: createDealDocument } = useActionHook(createDocumentAction, {
    onSuccess: (data) => {
      if (data.deal_id) {
        refetch(true);
        navigate({
          route: "documentId",
          params: {
            document_id: data.id,
            tab: "deals"
          },
          search: { dealId: data.deal_id }
        });
      }
    }
  });

  const onAddDealDocument = () => {
    handleClose();
    createModal({
      action: async (title: string) => {
        selectDealFolderModal({
          isOpen: true,
          title: "Move Document",
          isGeneralDocument: true,
          onSubmit: async ({ deal, selectedFolder }) => {
            if (!deal || !selectedFolder) return;
            const resultDataToSubmit: ICreateDocumentData = {
              title,
              deal_id: deal.id
            };

            if (selectedFolder.id !== 0) {
              resultDataToSubmit.section_id = selectedFolder.id;
            }

            if (selectedFolder.id === 0) {
              resultDataToSubmit.section_title = selectedFolder.title;
            }

            if (selectedFolder.id === 0 && selectedFolder.parent_id) {
              resultDataToSubmit.parent_section_id = selectedFolder.parent_id;
            }
            await createDealDocument(resultDataToSubmit);
          }
        });
      },
      title: "Add a New Document",
      submitButtonTitle: "Add"
    });
  };

  const onAddDocument = () => {
    handleClose();
    createModal({
      action: async (title: string) => {
        await createGlobalDocument(title);
      },
      title: "Add a New Document",
      submitButtonTitle: "Add"
    });
  };

  // const onAddDocument = useCallback(() => {
  //   handleClose();
  //   createModal({
  //     action: async (title: string) => {
  //       await createGlobalDocument(title);
  //     },
  //     title: "Add a new document",
  //     submitButtonTitle: "Add"
  //   });
  // }, [createModal, createGlobalDocument]);

  const onAddDeal = useCallback(async () => {
    handleClose();
    const data = await openPrompt({ Component: DealCreate, props: {} });
    if (!data) return;

    navigate({ route: "deal", params: { deal_id: data.id } });
  }, [openPrompt]);

  return (
    <>
      <IconButton
        size="sm"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ bgcolor: "#071F35", color: "#FFFFFF", ":hover": { bgcolor: "#071F35", color: "#FFFFFF" } }}
      >
        <BigPlusIcon />
      </IconButton>
      <Menu
        sx={{ ".MuiPaper-root": { borderRadius: "8px", mt: "3px" } }}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={handleClose}
      >
        <StyledMenuItem onClick={isDealsDocumentsTab ? onAddDealDocument : onAddDocument}>Document</StyledMenuItem>
        {isDealsDocumentsTab && <StyledMenuItem onClick={onAddDeal}>Deal</StyledMenuItem>}
      </Menu>
    </>
  );
};
